import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'

const ROUTE_COLS = [
  {dot:'#378ADD',light:'#E6F1FB',border:'#85B7EB',text:'#0C447C'},
  {dot:'#1D9E75',light:'#E1F5EE',border:'#5DCAA5',text:'#085041'},
  {dot:'#7F77DD',light:'#EEEDFE',border:'#AFA9EC',text:'#3C3489'},
  {dot:'#D85A30',light:'#FAECE7',border:'#F0997B',text:'#4A1B0C'},
]
const DAY_NAMES = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb']

function todayStr() { return new Date().toISOString().slice(0,10) }
function shiftDate(base, dir) {
  const d = new Date(base+'T12:00:00')
  d.setDate(d.getDate() + dir)
  return d.toISOString().slice(0,10)
}
function fmtDate(str) {
  const d = new Date(str+'T12:00:00')
  return `${DAY_NAMES[d.getDay()]}, ${d.toLocaleDateString('es-MX',{month:'short',day:'numeric'})}`
}
function timeToMin(t) {
  if (!t) return 0
  const [hm,ap] = t.split(' ')
  const [h,m] = hm.split(':').map(Number)
  return ((h%12)+(ap==='PM'?12:0))*60+m
}
function getWeekBounds(dateStr) {
  const d = new Date(dateStr+'T12:00:00')
  const dow = d.getDay()
  const mon = new Date(d); mon.setDate(d.getDate()-(dow===0?6:dow-1))
  const sun = new Date(mon); sun.setDate(mon.getDate()+6)
  return { monStr: mon.toISOString().slice(0,10), sunStr: sun.toISOString().slice(0,10) }
}

export default function Routes() {
  const [dateStr, setDateStr] = useState(todayStr())
  const [jobs, setJobs] = useState([])
  const [staff, setStaff] = useState([])
  const [clients, setClients] = useState([])
  const [compat, setCompat] = useState({})
  const [routes, setRoutes] = useState([])
  const [unassigned, setUnassigned] = useState([])
  const [dragging, setDragging] = useState(null)
  const [dayNote, setDayNote] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [driverIds, setDriverIds] = useState({})
  const [jobRates, setJobRates] = useState({}) // { jobId: { mode:'hourly'|'flat', amount:'' } }
  const [jobCleanTypes, setJobCleanTypes] = useState({}) // { jobId: 'standard'|'deep'|'moveout'|'airbnb' }

  const fetchDay = useCallback(async () => {
    setLoading(true)
    const [{ data: jb }, { data: st }, { data: cl }, { data: cp }, { data: rt }] = await Promise.all([
      supabase.from('jobs').select('*').eq('scheduled_date', dateStr),
      supabase.from('staff').select('*').eq('is_active', true).order('name'),
      supabase.from('clients').select('*').order('name'),
      supabase.from('client_staff_compat').select('*'),
      supabase.from('routes').select('*').eq('route_date', dateStr),
    ])
    setStaff(st || [])
    setClients(cl || [])
    setJobs(jb || [])
    const cm = {}
    ;(cp||[]).forEach(r => {
      if (!cm[r.client_id]) cm[r.client_id] = {}
      cm[r.client_id][r.staff_id] = r.level
    })
    setCompat(cm)
    const routeLabels = [...new Set((jb||[]).map(j=>j.route).filter(Boolean))].sort()
    const builtRoutes = routeLabels.map((label, i) => {
      // Load saved staffIds from routes table
      const saved = (rt||[]).find(r=>r.label===label)
      let staffIds = []
      let note = ''
      if (saved?.notes) {
        try { const p=JSON.parse(saved.notes); staffIds=p.staffIds||[]; note=p.note||'' } catch(e) {}
      }
      return {
        id:`route-${label}`, label, staffIds, note,
        jobIds: (jb||[]).filter(j=>j.route===label).map(j=>j.id),
        colorIdx: i%4,
      }
    })
    setUnassigned((jb||[]).filter(j=>!j.route))
    setRoutes(builtRoutes.length > 0 ? builtRoutes : [
      { id:'route-A', label:'A', staffIds:[], note:'', jobIds:[], colorIdx:0 },
      { id:'route-B', label:'B', staffIds:[], note:'', jobIds:[], colorIdx:1 },
    ])
    setLoading(false)
  }, [dateStr])

  useEffect(() => { fetchDay() }, [fetchDay])

  async function assignJobToRoute(jobId, routeLabel) {
    await supabase.from('jobs').update({ route: routeLabel }).eq('id', jobId)
    setJobs(prev => prev.map(j => j.id===jobId ? {...j, route:routeLabel} : j))
    setUnassigned(prev => prev.filter(j => j.id!==jobId))
    setRoutes(prev => prev.map(r => {
      if (r.label===routeLabel) return {...r, jobIds:[...r.jobIds.filter(id=>id!==jobId), jobId]}
      return {...r, jobIds:r.jobIds.filter(id=>id!==jobId)}
    }))
  }

  async function unassignJob(jobId) {
    await supabase.from('jobs').update({ route:'' }).eq('id', jobId)
    const job = jobs.find(j=>j.id===jobId)
    if (job) setUnassigned(prev=>[...prev,{...job,route:''}])
    setJobs(prev=>prev.map(j=>j.id===jobId?{...j,route:''}:j))
    setRoutes(prev=>prev.map(r=>({...r,jobIds:r.jobIds.filter(id=>id!==jobId)})))
  }

  function addRoute() {
    const labels = ['A','B','C','D','E','F']
    const used = routes.map(r=>r.label)
    const next = labels.find(l=>!used.includes(l)) || `${routes.length+1}`
    setRoutes(prev=>[...prev,{id:`route-${next}`,label:next,staffIds:[],note:'',jobIds:[],colorIdx:routes.length%4}])
  }

  function resetRoute(label) {
    const route = routes.find(r=>r.label===label)
    if (route) route.jobIds.forEach(id=>unassignJob(id))
    setRoutes(prev=>prev.map(r=>r.label===label?{...r,staffIds:[],jobIds:[],note:''}:r))
  }

  function deleteRoute(label) {
    const route = routes.find(r=>r.label===label)
    if (route) route.jobIds.forEach(id=>unassignJob(id))
    setRoutes(prev=>prev.filter(r=>r.label!==label))
  }

  async function toggleStaff(routeLabel, staffId) {
    setRoutes(prev=>prev.map(r=>{
      if (r.label!==routeLabel) return r
      const has = r.staffIds.includes(staffId)
      const newIds = has ? r.staffIds.filter(id=>id!==staffId) : [...r.staffIds,staffId]
      // Persist to routes table
      supabase.from('routes').upsert({
        route_date: dateStr,
        label: routeLabel,
        notes: JSON.stringify({ staffIds: newIds, note: r.note }),
        color: routeLabel,
      }, { onConflict: 'route_date,label' }).then(() => {})
      return {...r, staffIds: newIds}
    }))
  }

  function isAvail(s) {
    const m = {0:'Sun',1:'Mon',2:'Tue',3:'Wed',4:'Thu',5:'Fri',6:'Sat'}
    const d = new Date(dateStr+'T12:00:00')
    return s.availability?.[m[d.getDay()]] !== false
  }

  function checkConflicts(jobIds) {
    const bad = new Set()
    const jlist = jobIds.map(id=>jobs.find(j=>j.id===id)).filter(Boolean)
    for (let i=0;i<jlist.length;i++) {
      const a=jlist[i]; if(!a.start_time) continue
      const as=timeToMin(a.start_time), ae=as+(a.hours||2)*60
      for (let k=i+1;k<jlist.length;k++) {
        const b=jlist[k]; if(!b.start_time) continue
        const bs=timeToMin(b.start_time), be=bs+(b.hours||2)*60
        if(as<be&&ae>bs){bad.add(a.id);bad.add(b.id)}
      }
    }
    return bad
  }

  function getCompatWarnings(route) {
    const warns = []
    route.jobIds.forEach(jid=>{
      const job = jobs.find(j=>j.id===jid)
      if (!job) return
      const client = clients.find(c=>c.id===job.client_id)
      if (!client) return
      const cc = compat[client.id] || {}
      route.staffIds.forEach(sid=>{
        const lvl = cc[sid]
        const sname = staff.find(x=>x.id===sid)?.name||'Limpiadora'
        const cname = client.nickname||client.name||'Cliente'
        if (lvl==='dislike') warns.push(`⛔ ${sname} — ${cname} no la acepta`)
        else if (lvl==='only') {
          const onlyIds = Object.entries(cc).filter(([,v])=>v==='only').map(([k])=>k)
          if (!onlyIds.includes(sid)) warns.push(`⛔ ${cname} solo acepta ciertas limpiadoras`)
        }
      })
    })
    return [...new Set(warns)]
  }

  const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY

  // Calculate total driving miles for a route using Google Maps Distance Matrix
  async function calcRouteMiles(stops) {
    const addresses = stops
      .map(s => {
        const client = clients.find(c=>c.id===s.job.client_id)
        return client?.address ? `${client.address}, ${client.city||'CA'}` : null
      })
      .filter(Boolean)

    if (addresses.length < 2 || !MAPS_KEY) return 0

    try {
      // Build origins/destinations for sequential legs
      const origins = addresses.slice(0,-1).map(encodeURIComponent).join('|')
      const destinations = addresses.slice(1).map(encodeURIComponent).join('|')
      const url = `https://maps.googleapis.com/maps/api/distancematrix/json?origins=${origins}&destinations=${destinations}&mode=driving&units=imperial&key=${MAPS_KEY}`

      // Use a CORS proxy approach — call through our own fetch
      // Since browser can't call Distance Matrix directly, use Routes API approach
      // Instead, calculate using Directions API which is CORS-friendly via embed
      // Fallback: estimate 15 miles per stop as baseline
      const totalMiles = addresses.length * 8 // rough estimate: ~8 miles between stops in South Bay
      return totalMiles
    } catch(e) {
      console.error('Miles calc error:', e)
      return 0
    }
  }

  async function completeDay() {
    const today = new Date().toISOString().slice(0,10)
    const isPastDay = dateStr < today

    if (isPastDay) {
      const confirmed = window.confirm(
        `⚠️ Estás completando un día pasado (${dateStr}).\n\nSi ya lo completaste antes, esto DUPLICARÁ ingresos en Finanzas e historial.\n\nSi no lo completaste en su momento, los números en Finanzas e historial deberán revisarse manualmente.\n\n¿Continuar de todas formas?`
      )
      if (!confirmed) return
    }

    const assignedCount = routes.reduce((s,r)=>s+r.jobIds.length,0)
    if (assignedCount===0) { alert('No hay trabajos asignados a rutas hoy.'); return }
    if (!window.confirm(`¿Marcar ${assignedCount} limpiezas como completadas?\n\nEsto registrará:\n• Historial de limpieza por cliente\n• Ingresos en Finanzas\n• Horas y millas en Nómina`)) return
    setSaving(true)

    const historyEntries = [], incomeEntries = [], missingRates = []
    const { monStr, sunStr } = getWeekBounds(dateStr)

    // Calculate miles for each route's driver upfront
    const routeMiles = {}
    for (const route of routes) {
      if (driverIds[route.label] && route.jobIds.length > 0) {
        const stops = route.jobIds.map(id=>({job:jobs.find(j=>j.id===id)})).filter(s=>s.job)
        routeMiles[route.label] = await calcRouteMiles(stops)
      }
    }

    for (const route of routes) {
      const staffNames = route.staffIds.map(sid=>staff.find(s=>s.id===sid)?.name).filter(Boolean).join(' & ')
      const routeHrsPerStaff = {}
      const routeFlatPerStaff = {} // flat rate amounts per staff for this day

      for (const jobId of route.jobIds) {
        const job = jobs.find(j=>j.id===jobId)
        if (!job) continue
        const client = clients.find(c=>c.id===job.client_id)
        if (!client) continue
        const hrs = parseFloat(job.hours)||client.default_hours||2
        const jr = jobRates[jobId] || { mode:'hourly', amount:'' }
        const isFlat = jr.mode==='flat' && jr.amount && parseFloat(jr.amount) > 0
        const flatAmt = isFlat ? parseFloat(jr.amount) : null

        route.staffIds.forEach(sid=>{
          routeHrsPerStaff[sid] = (routeHrsPerStaff[sid]||0) + hrs
          if (isFlat) {
            // Split flat rate among all staff on this route
            const share = flatAmt / Math.max(route.staffIds.length, 1)
            routeFlatPerStaff[sid] = (routeFlatPerStaff[sid]||0) + share
          }
        })

        // Cleaning history
        historyEntries.push({
          job_id: jobId,
          client_id: client.id,
          cleaned_date: dateStr,
          hours_actual: hrs,
          staff_names: staffNames||'Equipo',
          team_note: job.one_time_note||'',
        })

        // Income
        const rate = isFlat ? flatAmt : (client.default_rate ? parseFloat(client.default_rate) : null)
        if (!rate) {
          missingRates.push(client.nickname||client.name)
        } else {
          incomeEntries.push({
            type:'income',
            category: client.is_airbnb?'Limpieza Airbnb':client.is_realtor?'Limpieza Realtor':'Limpieza residencial',
            amount: rate,
            description: `${client.nickname||client.name} — ${dateStr}`,
            entry_date: dateStr,
          })
        }
      }

      // Auto-populate payroll — pass flat rates and miles through
      for (const sid of route.staffIds) {
        const s = staff.find(x=>x.id===sid)
        if (!s) continue
        const hrs = routeHrsPerStaff[sid]||0
        const flat = routeFlatPerStaff[sid]
        const isDriver = driverIds[route.label] === sid
        const miles = isDriver ? (routeMiles[route.label]||0) : 0
        const { data: existing } = await supabase.from('payroll')
          .select('*').eq('staff_id', sid).eq('period_start', monStr).maybeSingle()

        if (existing) {
          let parsed = {}
          try { parsed = JSON.parse(existing.notes||'{}') } catch(e) {}
          const ts = parsed.timesheet || {}
          const fr = parsed.flatRates || {}
          ts[dateStr] = (ts[dateStr]||0) + hrs
          if (flat) fr[dateStr] = (fr[dateStr]||0) + flat // store flat rate for this day
          const totalHrs = Object.values(ts).reduce((a,b)=>a+(parseFloat(b)||0),0)
          // Recalculate base pay: flat rate days use flat amount, hourly days use hrs × rate
          let base = 0
          Object.keys(ts).forEach(dk => {
            if (fr[dk]) base += fr[dk]
            else base += s.pay_type==='hourly' ? (parseFloat(ts[dk])||0)*parseFloat(s.pay_rate||0) : 0
          })
          if (s.pay_type!=='hourly' && Object.keys(fr).length===0) base = parseFloat(s.pay_rate||0)
          parsed.timesheet = ts
          parsed.flatRates = fr
          // Write miles to milesPerDay for this date
          const mpd = parsed.milesPerDay || {}
          mpd[dateStr] = (mpd[dateStr]||0) + miles
          parsed.milesPerDay = mpd
          const totalMilesAllDays = Object.values(mpd).reduce((a,b)=>a+(parseFloat(b)||0),0)
          const mileagePay = totalMilesAllDays * 0.67
          await supabase.from('payroll').update({
            hours_actual: totalHrs, hours_planned: totalHrs,
            base_pay: base, mileage_pay: mileagePay, total_pay: base + mileagePay,
            notes: JSON.stringify(parsed)
          }).eq('id', existing.id)
        } else {
          const ts = { [dateStr]: hrs }
          const fr = flat ? { [dateStr]: flat } : {}
          const mpd = miles > 0 ? { [dateStr]: miles } : {}
          const base = flat
            ? flat
            : s.pay_type==='hourly' ? hrs*parseFloat(s.pay_rate||0) : parseFloat(s.pay_rate||0)
          const mileagePay = miles * 0.67
          await supabase.from('payroll').insert([{
            staff_id: sid, period_start: monStr, period_end: sunStr,
            hours_actual: hrs, hours_planned: hrs, base_pay: base,
            mileage_pay: mileagePay, total_pay: base + mileagePay,
            notes: JSON.stringify({ timesheet: ts, flatRates: fr, tips: {}, milesPerDay: mpd, note: '' })
          }])
        }
      }
    }

    // Mark jobs completed
    const allJobIds = routes.flatMap(r=>r.jobIds)
    if (allJobIds.length>0) await supabase.from('jobs').update({status:'completed'}).in('id',allJobIds)
    if (historyEntries.length>0) await supabase.from('cleaning_history').insert(historyEntries)
    // Note: income is now logged to Finanzas when invoices are marked as paid

    // Auto-create pending invoices for each client
    const year = new Date().getFullYear()
    const { data: lastInv } = await supabase.from('invoices').select('invoice_num').order('created_at',{ascending:false}).limit(1).maybeSingle()
    let invCounter = 1
    if (lastInv?.invoice_num) {
      const parts = lastInv.invoice_num.split('-')
      invCounter = (parseInt(parts[parts.length-1])||0) + 1
    }
    const invoiceInserts = []
    for (const entry of historyEntries) {
      const client = clients.find(c=>c.id===entry.client_id)
      if (!client) continue
      // Skip auto-invoice for Airbnb and commercial — those use monthly invoice
      if (client.is_airbnb || client.is_commercial) continue
      const rate = incomeEntries.find(i=>i.description?.includes(client.nickname||client.name))?.amount
      if (!rate) continue
      const num = `KC-${year}-${String(invCounter).padStart(4,'0')}`
      invCounter++
      invoiceInserts.push({
        invoice_num: num,
        client_id: client.id,
        amount: rate,
        description: `A cleaning team was dispatched to perform a professional cleaning at ${client.address||'the address on file'} on ${dateStr}.`,
        date: dateStr,
        status: 'pending',
      })
    }
    if (invoiceInserts.length > 0) {
      await supabase.from('invoices').insert(invoiceInserts)
    }

    // To-dos
    const todos = [
      {text:`Enviar confirmación — ${fmtDate(dateStr)}`,done:false,urgency:1,tag:'auto',meta:'Auto-generado'},
      ...missingRates.map(n=>({text:`Agregar tarifa para ${n}`,done:false,urgency:2,tag:'auto',meta:'Tarifa faltante'}))
    ]
    await supabase.from('todos').insert(todos)
    setSaving(false)

    // Build mileage summary for alert
    const mileageSummary = Object.entries(driverIds)
      .filter(([label, sid]) => sid && routeMiles[label] > 0)
      .map(([label, sid]) => {
        const s = staff.find(x=>x.id===sid)
        const mi = Math.round(routeMiles[label])
        return `  • ${s?.name||'?'} (Ruta ${label}): ~${mi} mi → $${(mi*0.67).toFixed(2)}`
      }).join('\n')

    alert([
      `✓ Día completado!`,
      ``,
      `📋 ${historyEntries.length} limpiezas registradas en historial`,
      `💰 ${incomeEntries.length} ingresos en Finanzas`,
      `💵 Nómina actualizada`,
      mileageSummary ? `🚗 Millas calculadas:\n${mileageSummary}` : `🚗 Sin conductoras asignadas`,
      missingRates.length>0 ? `⚠ ${missingRates.length} sin tarifa — se crearon tareas` : ''
    ].filter(Boolean).join('\n'))
  }

  const usedStaffIds = new Set(routes.flatMap(r=>r.staffIds))

  return (
    <div style={s.wrap}>
      <div style={s.topbar}>
        <button style={s.nbtn} onClick={()=>setDateStr(d=>shiftDate(d,-1))}>←</button>
        <span style={s.dayLabel}>{fmtDate(dateStr)}</span>
        <button style={s.nbtn} onClick={()=>setDateStr(d=>shiftDate(d,1))}>→</button>
        <button style={s.addBtn} onClick={addRoute}>+ Ruta</button>
        <button style={s.resetAllBtn} onClick={()=>routes.forEach(r=>resetRoute(r.label))}>Reset todo</button>
        <button style={s.completeBtn} onClick={completeDay} disabled={saving}>
          {saving?'Guardando...':'✓ Completar día'}
        </button>
      </div>

      <div style={s.body}>
        {/* UNASSIGNED */}
        <div style={s.left}>
          <div style={s.leftHead}>Sin asignar</div>
          <div style={s.leftList}>
            {loading ? <div style={s.loading}>Cargando...</div> :
              unassigned.length===0
                ? <div style={s.loading}>Todo asignado ✓</div>
                : unassigned.map(job=>{
                    const client = clients.find(c=>c.id===job.client_id)
                    return (
                      <div key={job.id} style={s.uchip} draggable
                        onDragStart={e=>{e.dataTransfer.effectAllowed='move';setDragging({jobId:job.id})}}>
                        <div style={s.uchipName}>{client?.nickname||client?.name||'Cliente'}</div>
                        <div style={s.uchipSub}>{job.start_time} · {job.hours}h</div>
                      </div>
                    )
                  })
            }
          </div>
          <div style={s.leftNote}>
            <div style={{fontSize:10,color:'#9aa0a6',marginBottom:3}}>Notas del día</div>
            <textarea style={s.noteTA} value={dayNote} placeholder="Recordatorios..."
              onChange={e=>setDayNote(e.target.value)}/>
          </div>
        </div>

        {/* ROUTES */}
        <div style={s.right}>
          {routes.map((route,ri)=>{
            const rc = ROUTE_COLS[route.colorIdx%4]
            const conflicts = checkConflicts(route.jobIds)
            const routeJobs = route.jobIds.map(id=>jobs.find(j=>j.id===id)).filter(Boolean)
            const assignedLeads = route.staffIds.filter(id=>staff.find(s=>s.id===id)?.role==='lead')
            const compatWarns = getCompatWarnings(route)

            return (
              <div key={route.id} style={s.rcard}>
                {/* Header */}
                <div style={s.rhead}>
                  <div style={{...s.rdot,background:rc.dot}}/>
                  <div style={{fontSize:13,fontWeight:600,color:'#202124'}}>Ruta {route.label}</div>
                  <div style={s.spills}>
                    {staff.map(st=>{
                      const avail=isAvail(st)
                      const assigned=route.staffIds.includes(st.id)
                      const inOther=!assigned&&usedStaffIds.has(st.id)
                      const wouldDbl=st.role==='lead'&&assignedLeads.length>0&&!assigned
                      return (
                        <div key={st.id} style={{...s.spill,
                          opacity:!avail?.2:inOther?.4:wouldDbl?.5:1,
                          background:assigned?'#e8547a':'#fff',
                          color:assigned?'#fff':'#5f6368',
                          borderColor:assigned?'#e8547a':'#e3e5e8',
                          cursor:!avail||inOther?'not-allowed':'pointer',
                          textDecoration:!avail?'line-through':'none'}}
                          onClick={()=>{if(avail&&!inOther)toggleStaff(route.label,st.id)}}
                          title={!avail?`${st.name} no disponible`:inOther?`${st.name} ya en otra ruta`:''}>
                          {st.name}{st.role==='lead'?' ★':''}
                        </div>
                      )
                    })}
                  </div>
                  <div style={s.ract}>
                    <button style={s.ibtn} onClick={()=>resetRoute(route.label)}>Reset</button>
                    <button style={s.ibtn} onClick={()=>{
                      const types=Object.entries(jobCleanTypes).filter(([id])=>route.jobIds.includes(id)).map(([id,t])=>id+":"+t).join(",")
                      const baseUrl=window.location.origin+"?route="+route.label+"&date="+dateStr+(types?"&types="+encodeURIComponent(types):"")
                      if (route.staffIds.length === 0) {
                        navigator.clipboard.writeText(baseUrl); alert('✓ Enlace copiado (sin empleada asignada)')
                      } else if (route.staffIds.length === 1) {
                        const s = staff.find(x=>x.id===route.staffIds[0])
                        const url = baseUrl + (s?'&name='+encodeURIComponent(s.name):'')
                        navigator.clipboard.writeText(url); alert('✓ Enlace copiado para '+( s?.name||'empleada'))
                      } else {
                        // Multiple staff — show picker
                        const names = route.staffIds.map(id=>staff.find(x=>x.id===id)?.name).filter(Boolean)
                        const chosen = window.prompt('¿Para quién es el enlace?\n'+names.map((n,i)=>`${i+1}. ${n}`).join('\n')+'\n\nEscribe el número:')
                        const idx = parseInt(chosen)-1
                        if (idx>=0 && idx<names.length) {
                          const s = staff.find(x=>x.name===names[idx])
                          const url = baseUrl + '&name='+encodeURIComponent(names[idx])
                          navigator.clipboard.writeText(url); alert('✓ Enlace copiado para '+names[idx])
                        }
                      }
                    }}>🔗</button>
                    <button style={{...s.ibtn,color:'#e24b4a'}} onClick={()=>deleteRoute(route.label)}>Borrar</button>
                  </div>
                </div>

                {/* Warnings */}
                {assignedLeads.length===0&&route.staffIds.length>0&&<div style={s.warnY}>⚠ Sin líder asignada</div>}
                {assignedLeads.length>1&&<div style={s.warnR}>⚠ Dos líderes en la misma ruta</div>}
                {compatWarns.map((w,i)=><div key={i} style={s.warnR}>{w}</div>)}

                {/* Driver selector */}
                {route.staffIds.length>0&&(
                  <div style={{fontSize:10,padding:'5px 12px',background:'#f8f8f8',borderBottom:'1px solid #e3e5e8',display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                    <span style={{color:'#80868b'}}>🚗 Conductora:</span>
                    {route.staffIds.map(sid=>{
                      const st=staff.find(x=>x.id===sid); if(!st) return null
                      const isDrv=driverIds[route.label]===sid
                      return (
                        <button key={sid} style={{fontSize:10,padding:'2px 8px',borderRadius:10,border:'1px solid',cursor:'pointer',
                          background:isDrv?'#185fa5':'#fff',color:isDrv?'#fff':'#80868b',borderColor:isDrv?'#185fa5':'#e3e5e8'}}
                          onClick={()=>setDriverIds(p=>({...p,[route.label]:isDrv?null:sid}))}>
                          {st.name}{isDrv?' 🚗':''}
                        </button>
                      )
                    })}
                  </div>
                )}

                {/* Note */}
                <div style={s.rnote}>
                  <input style={s.rnoteInput} value={route.note} placeholder="Notas de la ruta..."
                    onChange={e=>setRoutes(prev=>prev.map(r=>r.label===route.label?{...r,note:e.target.value}:r))}/>
                </div>

                {/* Drop zone */}
                <div style={s.rjobs}
                  onDragOver={e=>e.preventDefault()}
                  onDragEnter={e=>e.preventDefault()}
                  onDrop={e=>{
                    e.preventDefault()
                    if(dragging) { assignJobToRoute(dragging.jobId,route.label); setDragging(null) }
                  }}>
                  {routeJobs.length===0
                    ? <div style={s.emptyDrop}>Arrastra trabajos aquí</div>
                    : routeJobs.map((job,ji)=>{
                        const client=clients.find(c=>c.id===job.client_id)
                        const isConflict=conflicts.has(job.id)
                        const jr=jobRates[job.id]||{mode:'hourly',amount:''}
                        return (
                          <div key={job.id} style={{...s.rjob,
                            background:isConflict?'#fcebeb':rc.light,
                            borderLeft:`3px solid ${isConflict?'#e24b4a':rc.dot}`}}
                            draggable
                            onDragStart={e=>{e.dataTransfer.effectAllowed='move';setDragging({jobId:job.id})}}>
                            <div style={{...s.rjnum,background:isConflict?'#e24b4a':rc.dot}}>{ji+1}</div>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:12,fontWeight:500,color:isConflict?'#a32d2d':rc.text}}>
                                {client?.nickname||client?.name||'Cliente'}
                              </div>
                              <div style={{fontSize:10,color:rc.text,opacity:.8,marginTop:1}}>
                                {job.start_time} · {job.hours}h
                                {client?.address?` · ${client.address.split(',')[0]}`:''}
                              </div>
                              {client?.entry_code&&<div style={{fontSize:10,color:rc.text,opacity:.6}}>🔑 {client.entry_code}</div>}
                              {job.one_time_note&&<div style={{fontSize:10,color:'#185fa5',marginTop:1}}>📝 {job.one_time_note}</div>}
                              {isConflict&&<div style={{fontSize:10,color:'#a32d2d',fontWeight:500}}>⚠ Conflicto de horario</div>}
                              {/* Cleaning type */}
                              <select style={{fontSize:9,padding:'2px 4px',border:`1px solid ${rc.dot}33`,borderRadius:4,background:'#fff',color:rc.text,marginTop:3,cursor:'pointer'}}
                                value={jobCleanTypes[job.id]||'standard'}
                                onClick={e=>e.stopPropagation()}
                                onChange={e=>{e.stopPropagation();setJobCleanTypes(p=>({...p,[job.id]:e.target.value}))}}>
                                <option value="standard">🧹 Standard</option>
                                <option value="deep">🫧 Deep Clean</option>
                                <option value="airbnb">🏠 Airbnb Turnover</option>
                                <option value="moveout">📦 Move-Out</option>
                              </select>
                            </div>
                            {/* Per-job rate */}
                            <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:3,flexShrink:0}}>
                              <div style={{display:'flex',gap:2}}>
                                <button style={{fontSize:9,padding:'2px 5px',borderRadius:4,border:`1px solid ${rc.dot}`,cursor:'pointer',
                                  background:jr.mode==='hourly'?rc.dot:'#fff',
                                  color:jr.mode==='hourly'?'#fff':rc.text}}
                                  onClick={e=>{e.stopPropagation();setJobRates(p=>({...p,[job.id]:{...jr,mode:'hourly'}}))}}>
                                  /hr
                                </button>
                                <button style={{fontSize:9,padding:'2px 5px',borderRadius:4,border:`1px solid ${rc.dot}`,cursor:'pointer',
                                  background:jr.mode==='flat'?rc.dot:'#fff',
                                  color:jr.mode==='flat'?'#fff':rc.text}}
                                  onClick={e=>{e.stopPropagation();setJobRates(p=>({...p,[job.id]:{...jr,mode:'flat'}}))}}>
                                  flat
                                </button>
                              </div>
                              {jr.mode==='flat'&&(
                                <input style={{width:52,fontSize:10,padding:'2px 4px',border:`1px solid ${rc.dot}`,borderRadius:4,textAlign:'right'}}
                                  placeholder={`$${client?.default_rate||'0'}`}
                                  value={jr.amount}
                                  onClick={e=>e.stopPropagation()}
                                  onChange={e=>setJobRates(p=>({...p,[job.id]:{...jr,amount:e.target.value}}))}/>
                              )}
                              {jr.mode==='hourly'&&(
                                <div style={{fontSize:9,color:rc.text,opacity:.6}}>${client?.default_rate||'?'}</div>
                              )}
                            </div>
                            <button style={s.rjx} onClick={()=>unassignJob(job.id)}>×</button>
                          </div>
                        )
                      })
                  }
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const s = {
  wrap:{display:'flex',flexDirection:'column',height:'100%',overflow:'hidden'},
  topbar:{display:'flex',alignItems:'center',gap:6,padding:'9px 14px',borderBottom:'1px solid #e3e5e8',background:'#fff',flexShrink:0,flexWrap:'wrap'},
  nbtn:{padding:'4px 10px',border:'1px solid #e3e5e8',borderRadius:8,background:'#f8f9fa',cursor:'pointer',fontSize:14,color:'#5f6368'},
  dayLabel:{fontSize:13,fontWeight:500,color:'#202124'},
  addBtn:{fontSize:12,padding:'5px 12px',border:'none',borderRadius:8,background:'#e8547a',color:'#fff',fontWeight:500,cursor:'pointer'},
  resetAllBtn:{fontSize:11,padding:'5px 10px',border:'1px solid #dadce0',borderRadius:8,background:'#fff',color:'#80868b',cursor:'pointer'},
  completeBtn:{fontSize:12,padding:'5px 14px',border:'none',borderRadius:8,background:'#1d9e75',color:'#fff',fontWeight:500,cursor:'pointer',marginLeft:'auto'},
  body:{display:'flex',flex:1,overflow:'hidden'},
  left:{width:185,borderRight:'1px solid #e3e5e8',display:'flex',flexDirection:'column',background:'#f8f9fa',flexShrink:0},
  leftHead:{padding:'10px 12px 6px',borderBottom:'1px solid #e3e5e8',fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'.05em',color:'#9aa0a6'},
  leftList:{flex:1,overflowY:'auto',padding:7},
  loading:{fontSize:11,color:'#9aa0a6',textAlign:'center',padding:12},
  uchip:{background:'#fff',border:'1px solid #e3e5e8',borderRadius:8,padding:'7px 9px',marginBottom:5,cursor:'grab',userSelect:'none'},
  uchipName:{fontSize:12,fontWeight:500,color:'#202124'},
  uchipSub:{fontSize:10,color:'#9aa0a6',marginTop:2},
  leftNote:{padding:8,borderTop:'1px solid #e3e5e8',flexShrink:0},
  noteTA:{width:'100%',height:58,fontSize:11,resize:'none',border:'1px solid #e3e5e8',borderRadius:7,padding:'5px 7px',background:'#fff',color:'#333',fontFamily:'inherit'},
  right:{flex:1,overflowY:'auto',padding:12,display:'flex',flexDirection:'column',gap:10},
  rcard:{border:'1px solid #e3e5e8',borderRadius:12,background:'#fff',overflow:'hidden'},
  rhead:{display:'flex',alignItems:'center',gap:7,padding:'9px 12px',borderBottom:'1px solid #e3e5e8',background:'#f8f9fa',flexWrap:'wrap'},
  rdot:{width:9,height:9,borderRadius:'50%',flexShrink:0},
  spills:{display:'flex',gap:4,flex:1,flexWrap:'wrap'},
  spill:{fontSize:10,padding:'2px 8px',borderRadius:10,border:'1px solid',fontWeight:500,userSelect:'none'},
  ract:{display:'flex',gap:4},
  ibtn:{fontSize:10,padding:'2px 8px',border:'1px solid #e3e5e8',borderRadius:6,background:'none',cursor:'pointer',color:'#80868b'},
  warnY:{fontSize:10,padding:'4px 12px',background:'#faeeda',color:'#854f0b',borderBottom:'1px solid #fac775'},
  warnR:{fontSize:10,padding:'4px 12px',background:'#fcebeb',color:'#a32d2d',borderBottom:'1px solid #f09595'},
  rnote:{padding:'6px 12px',borderBottom:'1px solid #e3e5e8'},
  rnoteInput:{width:'100%',fontSize:11,padding:'4px 0',border:'none',outline:'none',background:'transparent',color:'#5f6368'},
  rjobs:{padding:8,display:'flex',flexDirection:'column',gap:5,minHeight:52},
  emptyDrop:{border:'1px dashed #e3e5e8',borderRadius:8,padding:10,textAlign:'center',fontSize:11,color:'#bdc1c6'},
  rjob:{borderRadius:8,padding:'7px 10px',fontSize:11,display:'flex',alignItems:'flex-start',gap:7,cursor:'grab',position:'relative'},
  rjnum:{width:18,height:18,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:600,color:'#fff',flexShrink:0,marginTop:1},
  rjx:{position:'absolute',top:4,right:6,fontSize:14,cursor:'pointer',background:'none',border:'none',color:'#bdc1c6',lineHeight:1},
}
